/* GrowthOS re-skin: Tickets grid columns. Actions column is now checkbox-only
   (48px pinned-left, no header label) matching leads/contacts/accounts/deals; the
   old OptionButton (edit/view/delete imgs) is commented out — view via the Subject
   link, delete via the bulk-bar. Cells restyled: Subject link, Status/Priority
   badges, Owner/Created By/Updated By avatar cells. Field/filter set unchanged. */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
} from '../../utils/agGridFilters';
import { isMobile } from '../../utils/deviceUtils';
import { DateFormatter, DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';

const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

// Status/Priority are admin-configurable master data — tone by case-insensitive substring.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('cancel') || s.includes('reject')) return 'red';
  if (s.includes('resolv') || s.includes('complet') || s.includes('done')) return 'green';
  if (s.includes('closed')) return 'gray';
  if (s.includes('progress') || s.includes('pending') || s.includes('hold')) return 'amber';
  if (s.includes('open') || s.includes('new') || s.includes('assign')) return 'blue';
  return 'gray';
};
const priorityTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('urgent') || s.includes('critical') || s.includes('high')) return 'red';
  if (s.includes('medium') || s.includes('normal')) return 'amber';
  if (s.includes('low')) return 'green';
  return 'gray';
};

const OwnerCell = (params) => {
  const name = NameFormatter ? NameFormatter(params.value) : params.value;
  if (!name) return null;
  return (
    <div className="gos-owner-cell">
      <span className="gos-ava sm" style={{ background: avaColor(name) }}>
        {initials(name)}
      </span>
      <span className="gos-owner-name">{name}</span>
    </div>
  );
};

const Badge = (tone) => (params) => {
  if (!params.value) return null;
  return (
    <span className={'gos-badge gos-b-' + tone(params.value)}>
      <span className="gos-pip" />
      {params.value}
    </span>
  );
};

const useTaskColumnDefs = ({ ticketStatusDropdown, ticketPriorityDropdown, owner, gridData }) => {
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);

  const actionsColumn = useMemo(
    () => [
      {
        headerName: '',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        checkboxSelection: (params) => params?.node && !params.node.group,
        headerCheckboxSelectionFilteredOnly: true,
        maxWidth: 48,
        minWidth: 48,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'left',
      },
    ],
    []
  );

  const columnDefs = useMemo(() => {
    if (!owner?.length || !ticketStatusDropdown?.length || !ticketPriorityDropdown?.length) return [];

    return [
      ...actionsColumn,
      {
        headerName: 'Subject',
        field: 'subject',
        pinned: isMobile() ? null : 'left',
        minWidth: 220,
        cellRenderer: (params) => {
          if (!params.value || params.node?.group) return params.value;
          return (
            <Link
              to={`/tickets/viewTicket?id=${params?.data?.id}`}
              state={params?.data}
              className="gos-lead-name"
            >
              {params.value}
            </Link>
          );
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Status',
        field: 'status',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(ticketStatusDropdown?.map((item) => item.displayName)),
        cellRenderer: Badge(statusTone),
      },
      {
        headerName: 'Priority',
        field: 'priority',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(ticketPriorityDropdown?.map((item) => item.displayName)),
        cellRenderer: Badge(priorityTone),
      },
      {
        headerName: 'Owner',
        field: 'owner',
        minWidth: 190,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
        cellRenderer: OwnerCell,
      },
      {
        headerName: 'Lead Name',
        field: 'leadName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Contact Name',
        field: 'contactName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'accountName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Opportunity Name',
        field: 'opportunityName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Campaign Name',
        field: 'campaignName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        valueFormatter: DateFormatter,
        minWidth: 200,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Reminder',
        field: 'reminder',
        filter: 'agDateColumnFilter',
        valueFormatter: DateTimeFormatter,
        minWidth: 200,
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Description',
        field: 'description',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        minWidth: 200,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: OwnerCell,
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        minWidth: 200,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: OwnerCell,
      },
      {
        headerName: 'Created Date Time',
        field: 'createdDateTime',
        valueFormatter: DateTimeFormatter,
        minWidth: 200,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        hide: true,
      },
      {
        headerName: 'Updated Date Time',
        field: 'updatedDateTime',
        valueFormatter: DateTimeFormatter,
        minWidth: 200,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        hide: true,
      },
    ];
  }, [
    owner,
    ticketPriorityDropdown,
    ticketStatusDropdown,
    gridData,
    actionsColumn,
    commonTextFilterParams,
    dateFilterParams,
    filterParams,
  ]);

  return columnDefs;
};

export default useTaskColumnDefs;
